import type { EditCommand, Edl } from '@repo/editor-core';
import { API_BASE_URL } from './rpc';
import { getAccessToken } from './auth';
import { apiErrorMessage, humanError } from '@/lib/api-error';

/** One transcript turn sent back to the agent (display-only items are omitted). */
export type ChatTurn = { role: 'user' | 'assistant'; content: string };

/**
 * A persisted turn as the server stores it — a {@link ChatTurn} plus the number
 * of timeline commands that turn applied, so the "N개 적용" card can be redrawn
 * after a reload.
 */
export type ChatMessage = ChatTurn & { edits?: number };

/**
 * Turns sent per request — mirrors the server's `agentChatSchema` cap. Restored
 * transcripts can reach it, so the composer trims to the newest turns instead of
 * letting the request 400.
 */
export const MAX_CHAT_TURNS = 80;

/** Events streamed from the agent loop (mirror of the server's AgentChatEvent). */
export type AgentEvent =
  | { type: 'thinking'; text: string }
  | { type: 'message'; text: string }
  | { type: 'edit'; command: EditCommand }
  | { type: 'tool'; name: string; status: string }
  | { type: 'notice'; text: string }
  /** `rev` 는 서버가 이 턴의 EDL 을 쓴 리비전이다(EditorPage 가 추적한다). */
  | { type: 'done'; applied: number; rev?: string }
  | { type: 'error'; message: string };

/**
 * Open the agent chat SSE stream and dispatch each parsed event to `onEvent`.
 * The endpoint streams `data: <json>` frames; each JSON payload is a discriminated
 * {@link AgentEvent}. Resolves when the stream ends (turn done) or aborts.
 */
export async function streamAgentChat(
  projectId: string,
  body: { messages: ChatTurn[]; edl: Edl },
  onEvent: (ev: AgentEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const token = getAccessToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/editor/projects/${projectId}/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (signal.aborted) return;
    onEvent({
      type: 'error',
      message: humanError(e, '연결이 끊겼어요. 잠시 뒤 다시 시도해 주세요.'),
    });
    return;
  }

  if (!res.ok || !res.body) {
    /*
     * 🔴 서버 원문을 그대로 띄우지 않는다. 예전에는 `String(j.error)` 라
     * 500 이면 대화창에 **`upstream failed`** 가 그대로 찍혔고(2026-09-11 확인),
     * 401 처럼 `error` 가 객체인 응답에서는 `[object Object]` 가 됐다.
     */
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      /* 본문이 JSON 이 아닐 수 있다 — 상태 코드만으로 답한다 */
    }
    onEvent({
      type: 'error',
      message: apiErrorMessage(
        res.status,
        json,
        '지금은 AI 에디터를 쓸 수 없어요. 잠시 뒤 다시 시도해 주세요.'
      ),
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // SSE frames are separated by a blank line.
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (!payload) continue;
        try {
          onEvent(JSON.parse(payload) as AgentEvent);
        } catch {
          /* skip a malformed frame */
        }
      }
    }
  } catch (e) {
    if (!signal.aborted) {
      onEvent({
        type: 'error',
        message: humanError(e, '대화가 중간에 끊겼어요. 다시 시도해 주세요.'),
      });
    }
  }
}
